import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import AddButton from "../../../components/AddButton";
import ConfirmDialog from "../../../components/Feedback/ConfirmDialog";
import ModalDetail from "../../../components/DetailModal";
import SearchAdvance from "../../../components/SearchAdvance";
import SearchAdvanceForm from "../../../components/SearchAdvanceForm";
import SearchBar from "../../../components/SearchBar";
import PaginatedTable from "../../../components/PaginatedTable";
import {
  getAllAdmins,
  deleteAdmin,
} from "../../../services/userServices/adminService";

const DataAdmin = () => {
  const [admins, setAdmins] = useState([]);
  const [rawAdmins, setRawAdmins] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [jabatan, setJabatan] = useState("");
  const [email, setEmail] = useState("");
  const [showFields, setShowFields] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [nama, setNama] = useState("");
  const [kode, setKode] = useState("");
  const [telp, setTelp] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  // Tambah state di atas
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedDeleteIndex, setSelectedDeleteIndex] = useState(null);

  const navigate = useNavigate();

  const columns = [
    { key: "nama", label: "Nama" },
    { key: "jabatan", label: "Jabatan" },
    { key: "kode", label: "Kode Karyawan" },
    { key: "telp", label: "No. Telp" },
    { key: "tanggalMasuk", label: "Tanggal Masuk" },
    { key: "email", label: "Email" },
  ];

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const users = await getAllAdmins();

        const adminsOnly = users.filter(
          (user) => user.user_type === "ADMIN" && user.is_active === true
        );

        const displayAdmins = adminsOnly.map((user) => ({
          id: user.id,
          nama: user.profiles?.full_name || "-",
          jabatan: user.employees?.position?.name || "-",
          jPengguna: user.profiles?.roles?.[0]?.name || "-",
          kode: user.employees?.employee_id || "-",
          alamat: user.profiles?.address,
          telp: user.profiles?.phone || "-",
          tanggalMasuk: user.employees?.join_date?.split("T")[0] || "-",
          email: user.email || "-",
        }));
        //console.log("Data dari API:", users);

        setAdmins(displayAdmins);
        setRawAdmins(adminsOnly);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  const filteredAdmins = admins.filter((admin) => {
    const masuk = admin.tanggalMasuk;

    if (fromDate && masuk < fromDate) return false;
    if (toDate && masuk > toDate) return false;
    if (nama && !admin.nama?.toLowerCase().includes(nama.toLowerCase()))
      return false;
    if (
      jabatan &&
      !admin.jabatan?.toLowerCase().includes(jabatan.toLowerCase())
    )
      return false;
    if (kode && !admin.kode?.toLowerCase().includes(kode.toLowerCase()))
      return false;
    if (email && !admin.email?.toLowerCase().includes(email.toLowerCase()))
      return false;
    if (telp && !admin.telp?.toLowerCase().includes(telp.toLowerCase()))
      return false;

    if (
      search &&
      ![
        admin.nama,
        admin.jabatan,
        admin.kode,
        admin.telp,
        admin.email,
        admin.tanggalMasuk,
      ].some((field) =>
        field?.toString().toLowerCase().includes(search.toLowerCase())
      )
    ) {
      return false;
    }

    return true;
  });

  const handleEdit = (index) => {
    const selected = filteredAdmins[index];
    const original = rawAdmins.find((a) => a.id === selected.id);
    localStorage.setItem("editAdmin", JSON.stringify(original));
    navigate("/edit-admin");
  };

  const handleDeleteAdmin = async (index) => {
    const selected = filteredAdmins[index];
    if (!selected) return;

    try {
      await deleteAdmin(selected.id);

      const updatedAdmins = admins.filter((a) => a.id !== selected.id);
      const updatedRawAdmins = rawAdmins.filter((a) => a.id !== selected.id);

      setAdmins(updatedAdmins);
      setRawAdmins(updatedRawAdmins);
      setSelectedAdmin(null); // reset pilihan
    } catch (error) {
      console.error("Gagal menghapus admin:", error);
      alert("Gagal menghapus admin.");
    }
  };

  const handleView = (index) => {
    const selected = filteredAdmins[index]; // ini hanya untuk ambil id-nya
    if (!selected) return;

    // Ambil data lengkap dari rawAdmins (bukan dari hasil filter)
    const original = rawAdmins.find((a) => a.id === selected.id);

    if (original) {
      // Mapping lagi kalau ModalDetail butuh data yang sudah diformat
      const detailData = {
        //id: original.id,
        nama: original.profiles?.full_name || "-",
        JenisPengguna: original.profiles?.roles?.[0]?.name || "-",
        jabatan: original.employees?.position?.name || "-",
        kode: original.employees?.employee_id || "-",
        telp: original.profiles?.phone || "-",
        tanggalMasuk: original.employees?.join_date?.split("T")[0] || "-",
        email: original.email || "-",
        alamat: original.profiles?.address,
        // kamu bisa tambahin properti lain dari rawAdmins di sini kalau perlu
      };

      setSelectedAdmin(detailData);
      setShowDetailModal(true);
    }
  };

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setNama("");
    setJabatan("");
    setKode("");
    setEmail("");
    setTelp("");
  };

  return (
    <div className="pt-10 pl-64">
      <h2 className="pl-6 mb-6 text-2xl font-semibold text-green-700">
        Data Admin
      </h2>
      <div className="p-6 mx-4 bg-white shadow rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <AddButton
            onClick={() => navigate("/add-admin")}
            moduleName="AdminData"
          />
          <div className="flex items-center gap-2">
            <SearchBar value={search} onChange={setSearch} />
            <SearchAdvance
              showFields={showFields}
              toggleShowFields={() => setShowFields(!showFields)}
            />
          </div>
        </div>

        {showFields && (
          <SearchAdvanceForm
            dateFields={[
              {
                label: "Tanggal Masuk",
                fromValue: fromDate,
                toValue: toDate,
                onFromChange: setFromDate,
                onToChange: setToDate,
              },
            ]}
            additionalFields={[
              { label: "Nama", value: nama, onChange: setNama },
              { label: "Jabatan", value: jabatan, onChange: setJabatan },
              { label: "Kode Karyawan", value: kode, onChange: setKode },
              { label: "No. Telp", value: telp, onChange: setTelp },
              { label: "Email", value: email, onChange: setEmail },
            ]}
            onClear={handleClearFilters}
          />
        )}

        {isLoading ? (
          <p className="py-8 text-center text-gray-500">Memuat data admin...</p>
        ) : (
          <PaginatedTable
            data={filteredAdmins}
            columns={columns}
            onEdit={handleEdit}
            onDelete={(index) => {
              setSelectedDeleteIndex(index);
              setIsConfirmOpen(true);
            }}
            onView={handleView}
            moduleName="AdminData"
          />
        )}
        <ConfirmDialog
          isOpen={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={() => {
            if (selectedDeleteIndex !== null) {
              handleDeleteAdmin(selectedDeleteIndex);
            }
          }}
          title="Hapus Admin"
          message="Apakah Anda yakin ingin menghapus data ini?"
        />

        {showDetailModal && (
          <ModalDetail
            data={selectedAdmin}
            onClose={() => setShowDetailModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DataAdmin;
